import * as React from 'react';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
    ApiOutlined,
    BulbOutlined,
    CodeOutlined,
    CommentOutlined,
    DisconnectOutlined,
    DislikeOutlined,
    FlagOutlined,
    LikeOutlined,
    ReadFilled,
    RiseOutlined,
} from '@ant-design/icons';
import { Citation, PotentialCause, PotentialCausesSummary } from '@/types';
import { antGold, antGrey, rwColors } from '@/components/styles/globalStyles';

const CAUSE_ICON: Record<PotentialCause, React.ReactNode> = {
    code: <CodeOutlined />,
    featureFlag: <FlagOutlined />,
    trafficSpike: <RiseOutlined />,
    dependency: <ApiOutlined />,
    integrationIssue: <DisconnectOutlined />,
};

const FEEDBACK_ICON_STYLE: React.CSSProperties = {
    color: antGrey.grey6,
    fontSize: 18,
    cursor: 'pointer',
};

const ROW_STYLE: React.CSSProperties = {
    margin: '0 16px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
};

const CitationBox: React.FC<{ citations: Citation[] }> = ({ citations }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: antGold.gold1,
            border: `1px solid ${antGold.gold3}`,
            borderRadius: 4,
        }}
    >
        <ReadFilled style={{ color: rwColors.statusYellow, fontSize: 16, flexShrink: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {citations.map((citation, index) => (
                <Text
                    key={`${citation.referenceId ?? citation.label}-${index}`}
                    style={{ color: antGrey.grey9, fontSize: 14 }}
                >
                    {citation.label}
                    {citation.url && (
                        <>
                            {', '}
                            <a
                                href={citation.url}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: antGrey.grey9, textDecoration: 'underline' }}
                            >
                                link to source
                            </a>
                        </>
                    )}
                </Text>
            ))}
        </div>
    </div>
);

export const PotentialCausesSection: React.FC<{ potentialCauses?: PotentialCausesSummary }> = ({ potentialCauses }) => {
    if (!potentialCauses?.causes?.length) {
        return null;
    }
    const citations = potentialCauses.causes.reduce<Citation[]>((acc, cause) => acc.concat(cause.citations ?? []), []);
    return (
        <Collapse
            className="triage-collapse triage-collapse-causes"
            ghost
            bordered={false}
            defaultActiveKey={['section']}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    label: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <BulbOutlined style={{ color: rwColors.statusYellow }} />
                            <Text style={{ color: rwColors.statusYellow, fontSize: 14, fontWeight: 600 }}>
                                Potential cause(s) identified
                            </Text>
                        </div>
                    ),
                    children: (
                        <>
                            <div style={ROW_STYLE}>
                                {potentialCauses.aiTitle && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <span style={{ color: antGrey.grey6, fontSize: 22, flexShrink: 0 }}>
                                            {CAUSE_ICON[potentialCauses.causes[0]?.cause]}
                                        </span>
                                        <Text style={{ fontSize: 20, color: antGrey.grey9 }}>
                                            {potentialCauses.aiTitle}
                                        </Text>
                                    </div>
                                )}
                                {potentialCauses.aiDescription && (
                                    <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                                        {potentialCauses.aiDescription}
                                    </Paragraph>
                                )}
                            </div>
                            {citations.length > 0 && (
                                <div style={{ ...ROW_STYLE, padding: '8px 0 20px' }}>
                                    <CitationBox citations={citations} />
                                </div>
                            )}
                            <div
                                style={{
                                    ...ROW_STYLE,
                                    paddingTop: 0,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 16,
                                }}
                            >
                                {/* No onClick: feedback interactions are attached to these icons in Userflow */}
                                <LikeOutlined style={FEEDBACK_ICON_STYLE} />
                                <DislikeOutlined style={FEEDBACK_ICON_STYLE} />
                                <CommentOutlined style={FEEDBACK_ICON_STYLE} />
                            </div>
                        </>
                    ),
                },
            ]}
        />
    );
};
